import { Criterion, CriterionValue } from "model/criterion";
import { ListingsCriteria } from "model/listings-criteria";
import { ListingSearchFieldType } from "type/listing-search-field-type";

/**
 * Convert Kestrel's 2D keywords array to Control Panel's delimited string format
 * Example: [["pool", "spa"], ["fireplace"]] → "pool,spa;fireplace"
 */
const keywordsArrayToString = (keywords: string[][]): string => {
    return keywords
        .map(group => group.join(",")) // Join each group with commas (OR)
        .join(";"); // Join groups with semicolons (AND)
}

/**
 * Convert Control Panel's delimited string to Kestrel's 2D array format
 * Example: "pool,spa;fireplace" → [["pool", "spa"], ["fireplace"]]
 */
const keywordsStringToArray = (keywords: string): string[][] => {
    return keywords
        .split(";") // Split by semicolons (AND)
        .map(group => group.split(",") // Split each group by commas (OR)
            .map(kw => kw.trim()) // Trim whitespace
            .filter(kw => kw.length > 0)) // Remove empty strings
        .filter(group => group.length > 0); // Remove empty groups
}

export const translateToCriterion = (listingCriteria: ListingsCriteria | undefined) => {
    const criteria: Criterion[] = [];
    for (let key in listingCriteria) {
		const typedKey = key as keyof typeof listingCriteria;
		let value = listingCriteria[typedKey];

		// Skip nls field - we have resolved criteria (keywords, city, etc.)
		// This prevents re-processing NLS queries with non-deterministic AI results
		if (key === "nls") {
			continue;
		}

		if (value !== undefined) {
			// Special handling for keywords: convert 2D array to delimited string
			if (key === "keywords" && Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
				value = keywordsArrayToString(value as unknown as string[][]);
			}

			const criterion: Criterion = {
				name: key,
				label: key,
				value: value as boolean | string | number | string[] | number[],
			}
			const isAreaField = !!ListingSearchFieldType.isArea(key);
			if (isAreaField) {
				criterion.area = isAreaField;
			}
			criteria.push(criterion);
		}
	}
    return criteria;
}

export const translateToListingsCriteria = (criteria: Criterion[] | undefined) => {
	if (!criteria) {
		return
	}
    const listingsCriteria: Partial<Record<string, CriterionValue>> = {};
    for (let criterion of criteria) {
        let value = criterion.value;

		// Skip boardId: 0 — it is a sentinel for "no region selected" and causes Kestrel to return no results
		if (criterion.name === "boardId" && value === 0) {
			continue;
		}

		// Special handling for keywords: convert delimited string to 2D array
		if (criterion.name === "keywords" && typeof value === "string") {
			value = keywordsStringToArray(value) as any;
		}

        listingsCriteria[criterion.name] = value;
    }
    return listingsCriteria as ListingsCriteria;
}
  